// src/components/kyb/forms/AnalysisForm.tsx
// CHANGES:
// 1. Layout changed to: Business Name (full width), then Country + Analyze button (equal width)
// 2. Added responsive grid layout

import React from 'react';
import { ShimmerButton } from '../../ui/ShimmerButton';
import CountryAutocomplete from '../../CountryAutocomplete';
import { Building2, Briefcase, User } from 'lucide-react';

interface AnalysisFormProps {
  businessName: string;
  setBusinessName: (name: string) => void;
  country: string;
  setCountry: (country: string) => void;
  entityType: string;
  setEntityType: (type: string) => void;
  website: string;
  setWebsite: (website: string) => void;
  isAnalyzing: boolean;
  onAnalyze: () => void;
}

export const AnalysisForm: React.FC<AnalysisFormProps> = ({
  businessName,
  setBusinessName,
  country,
  setCountry,
  entityType,
  setEntityType,
  website,
  setWebsite,
  isAnalyzing,
  onAnalyze
}) => {
  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-slate-200/50 overflow-visible 
                    hover:shadow-3xl transition-shadow duration-300">
      <div className="p-8 sm:p-10">
        <div className="space-y-6">
          {/* Row 1: Business Name + Country */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
            {/* Business Name - 3 columns */}
            <div className="md:col-span-3">
              <label className="block text-sm font-bold text-slate-800 uppercase tracking-wider mb-3 
                              flex items-center gap-2">
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full"></span>
                  <span className="w-3 h-0.5 bg-gradient-to-r from-blue-500 to-purple-600"></span>
                  <span className="w-1.5 h-1.5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full"></span>
                </span>
                Business Name
              </label>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Enter company name..."
                className="w-full px-6 py-4 border-2 border-purple-200/60 rounded-2xl 
                         bg-gradient-to-br from-purple-50/30 to-blue-50/30
                         focus:border-blue-400 focus:ring-4 focus:ring-blue-400/30 
                         focus:shadow-lg focus:shadow-blue-200/50
                         outline-none transition-all duration-300 text-slate-900 font-semibold text-lg
                         disabled:bg-slate-100 disabled:cursor-not-allowed
                         shadow-md shadow-slate-200/60 hover:shadow-lg hover:shadow-slate-300/60 hover:border-purple-300/70
                         placeholder:text-slate-400 placeholder:font-normal"
                disabled={isAnalyzing}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !isAnalyzing && businessName.trim()) {
                    onAnalyze();
                  }
                }}
              />
            </div>

            {/* Country - 2 columns */}
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-slate-800 uppercase tracking-wider mb-3 
                              flex items-center gap-2">
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full"></span>
                  <span className="w-3 h-0.5 bg-gradient-to-r from-blue-500 to-purple-600"></span>
                  <span className="w-1.5 h-1.5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full"></span>
                </span>
                Country / Region
              </label>
              <div className="shadow-md shadow-slate-200/60 hover:shadow-lg hover:shadow-slate-300/60 transition-shadow duration-300">
                <CountryAutocomplete
                  value={country}
                  onChange={(value) => setCountry(value as string)}
                  disabled={isAnalyzing}
                  placeholder="Select country..."
                />
              </div>
            </div>
          </div>

          {/* Row 2: Entity Type Buttons - Full Width */}
          <div>
            <label className="block text-sm font-bold text-slate-800 uppercase tracking-wider mb-3 
                            flex items-center gap-2">
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full"></span>
                <span className="w-3 h-0.5 bg-gradient-to-r from-blue-500 to-purple-600"></span>
                <span className="w-1.5 h-1.5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full"></span>
              </span>
              Entity Type
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setEntityType('any')}
                disabled={isAnalyzing}
                className={`px-5 py-4 rounded-2xl font-bold transition-all duration-300 border-2 
                          shadow-lg hover:shadow-2xl transform hover:scale-105 
                          flex items-center justify-center gap-2 ${
                  entityType === 'any'
                    ? 'bg-gradient-to-br from-indigo-400 via-purple-400 to-indigo-500 border-indigo-300 text-white shadow-indigo-400/60 hover:shadow-indigo-400/80'
                    : 'bg-gradient-to-br from-slate-50 to-slate-100 border-indigo-200/60 text-slate-700 hover:border-indigo-300 hover:from-indigo-50 hover:to-purple-50 hover:shadow-indigo-200/50'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
              >
                <Briefcase className="w-5 h-5" />
                <span>Any</span>
              </button>
              <button
                type="button"
                onClick={() => setEntityType('limited_company')}
                disabled={isAnalyzing}
                className={`px-5 py-4 rounded-2xl font-bold transition-all duration-300 border-2 
                          shadow-lg hover:shadow-2xl transform hover:scale-105 
                          flex items-center justify-center gap-2 ${
                  entityType === 'limited_company'
                    ? 'bg-gradient-to-br from-blue-400 via-cyan-400 to-blue-500 border-blue-300 text-white shadow-blue-400/60 hover:shadow-blue-400/80'
                    : 'bg-gradient-to-br from-slate-50 to-slate-100 border-blue-200/60 text-slate-700 hover:border-blue-300 hover:from-cyan-50 hover:to-blue-50 hover:shadow-blue-200/50'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
              >
                <Building2 className="w-5 h-5" />
                <span>Limited Co.</span>
              </button>
              <button
                type="button"
                onClick={() => setEntityType('sole_trader')}
                disabled={isAnalyzing}
                className={`px-5 py-4 rounded-2xl font-bold transition-all duration-300 border-2 
                          shadow-lg hover:shadow-2xl transform hover:scale-105 
                          flex items-center justify-center gap-2 ${
                  entityType === 'sole_trader'
                    ? 'bg-gradient-to-br from-teal-400 via-emerald-400 to-teal-500 border-teal-300 text-white shadow-teal-400/60 hover:shadow-teal-400/80'
                    : 'bg-gradient-to-br from-slate-50 to-slate-100 border-teal-200/60 text-slate-700 hover:border-teal-300 hover:from-emerald-50 hover:to-teal-50 hover:shadow-teal-200/50'
                } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
              >
                <User className="w-5 h-5" />
                <span>Sole Trader</span>
              </button>
            </div>
            {entityType === 'sole_trader' && (
              <p className="mt-3 text-sm text-amber-800 bg-gradient-to-r from-amber-50 to-yellow-50 
                          border-2 border-amber-300 rounded-2xl px-4 py-3 shadow-md shadow-amber-100/60">
                <span className="font-bold">ℹ️</span> Analysis focuses on web presence & domain verification (no Companies House registration)
              </p>
            )}
          </div>

          {/* Row 3: Website - Full Width with Helper Text */}
          <div>
            <label className="block text-sm font-bold text-slate-800 uppercase tracking-wider mb-3 
                            flex items-center gap-2">
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full"></span>
                <span className="w-3 h-0.5 bg-gradient-to-r from-blue-500 to-purple-600"></span>
                <span className="w-1.5 h-1.5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full"></span>
              </span>
              Website <span className="text-slate-400 font-normal lowercase">(Optional)</span>
            </label>
            <div className="flex items-start gap-3">
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="e.g., example.com or https://example.com"
                className="flex-1 px-6 py-4 border-2 border-purple-200/60 rounded-2xl 
                         bg-gradient-to-br from-purple-50/30 to-blue-50/30
                         focus:border-blue-400 focus:ring-4 focus:ring-blue-400/30 
                         focus:shadow-lg focus:shadow-blue-200/50
                         outline-none transition-all duration-300 text-slate-900 font-medium 
                         disabled:bg-slate-100 disabled:cursor-not-allowed
                         shadow-md shadow-slate-200/60 hover:shadow-lg hover:shadow-slate-300/60 hover:border-purple-300/70
                         placeholder:text-slate-400"
                disabled={isAnalyzing}
              />
              <div className="hidden md:flex items-center px-4 py-4 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 
                            rounded-2xl text-sm text-slate-600 shadow-md shadow-blue-100/60">
                <span className="text-blue-500 font-bold mr-2">💡</span>
                <span className="text-blue-700 font-medium">Helps verify identity</span>
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-600 md:hidden flex items-start gap-2">
              <span className="text-blue-500 font-bold">💡</span>
              <span>Providing a website helps verify the business identity and check online presence</span>
            </p>
          </div>

          {/* Row 4: Analyze Button - Full Width */}
          <div className="pt-2">
            <ShimmerButton
              onClick={onAnalyze}
              disabled={!businessName.trim() || isAnalyzing}
              isLoading={isAnalyzing}
              className="w-full h-[56px] text-lg font-bold shadow-2xl shadow-blue-500/30 hover:shadow-2xl hover:shadow-blue-500/50 
                       transition-all duration-300 transform hover:scale-[1.01]"
            >
              Analyze
            </ShimmerButton>
          </div>
        </div>
      </div>
    </div>
  );
};